import React, { useEffect, useState } from 'react'
import { TextField, RenderCurrencyFormatTextField, Button } from '@components/atoms'
import { Modal, FormControlLabel, Switch, Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import { ProductType, ProductUpdateType } from '@schemas'

interface ProductModificationModalProps {
	open: boolean
	onClose: () => void
	onSubmit: (product: ProductUpdateType) => void
	initialValues: ProductType
}

/** 제품 수정 — surface 패널 · radius 0 · 헤어라인, 블랙 CTA 하나 */
export const ProductModificationModal: React.FC<ProductModificationModalProps> = ({
	open,
	onClose,
	onSubmit,
	initialValues,
}) => {
	const [name, setName] = useState('')
	const [code, setCode] = useState('')
	const [usd, setUsd] = useState('')
	const [index, setIndex] = useState('')
	const [useYn, setUseYn] = useState(true)

	useEffect(() => {
		if (initialValues) {
			setName(initialValues.name)
			setCode(initialValues.code)
			setUsd(initialValues.usd.toString())
			setIndex(initialValues.index.toString())
			setUseYn(initialValues.useYn === 1)
		}
	}, [initialValues])

	const handleModify = () => {
		if (name && usd && index && initialValues.id !== '') {
			onSubmit({
				id: initialValues?.id || '',
				code,
				name,
				usd: Number(usd),
				krw: initialValues.krw,
				discountUsd: initialValues.discountUsd,
				discountKrw: initialValues.discountKrw,
				quantity: initialValues.quantity,
				index: Number(index),
				useYn: useYn ? 1 : 0,
				alwaysAvailableYn: initialValues.alwaysAvailableYn,
				soldoutYn: initialValues.soldoutYn,
			})
			onClose()
		}
	}

	return (
		<Modal open={open} onClose={onClose}>
			<Box component="div" sx={styles.modalContent}>
				<Box component="div" sx={styles.head}>
					<Box component="span" sx={styles.kicker}>
						Product
					</Box>
					<IconButton onClick={onClose} size="small" aria-label="close">
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
				<Box component="h2" sx={styles.title}>
					제품 수정
				</Box>
				<TextField
					label="제품명"
					variant="standard"
					value={name}
					onChange={e => setName(e.target.value)}
					fullWidth
					margin="normal"
				/>
				<TextField
					label="제품코드"
					variant="standard"
					value={code}
					onChange={e => setCode(e.target.value)}
					fullWidth
					margin="normal"
				/>
				<TextField
					label="가격 (USD)"
					variant="standard"
					value={usd}
					onChange={e => setUsd(e.target.value)}
					fullWidth
					margin="normal"
					InputProps={{
						inputComponent: RenderCurrencyFormatTextField,
					}}
				/>
				<TextField
					label="인덱스"
					variant="standard"
					value={index}
					onChange={e => setIndex(e.target.value)}
					fullWidth
					margin="normal"
				/>
				<FormControlLabel
					control={<Switch checked={useYn} onChange={e => setUseYn(e.target.checked)} />}
					label="사용 여부"
					sx={styles.switch}
				/>
				<Button onClick={handleModify} variant="contained" fullWidth>
					수정하기
				</Button>
			</Box>
		</Modal>
	)
}

const styles = {
	modalContent: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: 420,
		maxWidth: 'calc(100vw - 32px)',
		backgroundColor: mono.surface,
		border: `1px solid ${mono.line}`,
		boxShadow: '0 30px 60px rgba(20, 24, 32, 0.12)',
		padding: '24px 28px 28px',
		borderRadius: 0,
		textAlign: 'left',
		fontFamily: boFont.ui,
		outline: 'none',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	kicker: {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	title: {
		margin: '8px 0 12px',
		fontSize: 24,
		fontWeight: 300,
		lineHeight: 1.15,
		color: mono.ink,
	},
	switch: {
		marginTop: 2,
		marginBottom: 3,
		'.MuiFormControlLabel-label': { fontSize: 13, color: mono.dim },
	},
}
